import { LitElement, html } from 'lit';
import { classMap } from 'lit-html/directives/class-map.js';
import { FsIcon } from '@cof/fs-icon-wc/fs-icon.js';
import { fs24Application } from '@cof/fs-icons';
import { trackAnalyticsEvent } from '@cof/refi-web-sdk';
import { TagKey } from '@cof/tag-catalog-fs-vehicle-and-loan-mfe';
import { DebugService } from '../../../shared/services/debug.service.js';
import { currencyFormatter } from '../../../shared/formatters/currency.formatter.js';
import { REFI_VEHICLE_LOAN_CONST } from '../../../shared/constants/app.constants.js';
import { POLICY_LIMITS_CONST } from '../../../shared/services/policy-limits.service.js';
import { style } from './iconic-loan-card.style.js';
import { tags } from '../../../shared/constants/tags.js';
import { VehicleService } from '../../../services/vehicle.service.js';
import { LoanService } from '../../../services/loan.service.js';
import { ApplicationService } from '../../../services/application.service.js';
import { stratum } from '../../../../constants/stratum.js';
import '@cof/fs-helper-text-wc/fs-helper-text.js';
import { AppService } from '../../../app.service.js';

FsIcon.add(fs24Application);

class RefiIconicLoanCard extends LitElement {
  constructor() {
    super();
    this.debug = new DebugService('RefiIconicLoanCard');
    this.showButton = false;
    this.inline = false;
    this.inlineIneligible = false;
    this.showPlaceholder = false;
    this.hideIneligibility = false;
    this.tagBalance = true;
  }

  connectedCallback() {
    super.connectedCallback();
    if (!this.showPlaceholder) {
      this.debug.log('loaded with', this.loan);
      this.loanId = `loanButton${this.loanIndex}`;
      const wholeSaleValue = VehicleService.selectedVehicle?.wholeSaleValue;

      this.applicantModel = ApplicationService.Application;
      if (
        wholeSaleValue &&
        this.isHighLtv(this.loan.currentBalance, wholeSaleValue) &&
        this.loan.ineligibleReasons.indexOf('HIGH_LTV') === -1
      ) {
        this.loan.ineligibleReasons.push('HIGH_LTV');
      }
      if (
        wholeSaleValue &&
        !this.isHighLtv(this.loan.currentBalance, wholeSaleValue) &&
        this.loan.ineligibleReasons.indexOf('HIGH_LTV') !== -1
      ) {
        this.loan.ineligibleReasons.splice(
          this.loan.ineligibleReasons.indexOf('HIGH_LTV'),
          1
        );
      }
      this.isEligible = this.loan.ineligibleReasons.length === 0;
      if (!this.isEligible) {
        this.ineligibleLoanMessage = LoanService.getWarningText(this.loan);
        if (!this.inline) {
          this.ineligibleLoanMessage = `${this.ineligibleLoanMessage}.  If this loan is connected to the car you selected, you'll have to refinance a different car.`;
        }

        const upfrontOffer = ApplicationService.isUpfrontOffers;
        if (!upfrontOffer) {
          this.loan.isCapitalOneLoaned =
            this.loan.ineligibleReasons.indexOf('CAPITAL_ONE_LOAN') >= 0
              ? 'yes'
              : 'no';
          const loanPrequalInfo = LoanService.createPrequalInfoLoan(
            this.loan,
            this.ineligibleLoanMessage
          );
          AppService.saveVehicleLoanPrequalInfo(loanPrequalInfo);
          trackAnalyticsEvent(tags.loan.refileads);
        }
      }
      this.sendLoanAnalytics();
    } else {
      this.isEligible = true;
    }
  }

  static properties = {
    selected: { type: Boolean },
    isLoanIneligible: { type: Boolean },
    loan: { type: Object },
    loanIndex: { type: Number },
    payoffLabel: { type: String },
    ineligibleLoanMessage: { type: String },
    loanId: { type: String },
    inline: { type: Boolean },
    showButton: { type: Boolean },
    inlineIneligible: { type: Boolean, attribute: 'inline-ineligible' },
    showPlaceholder: { type: Boolean },
    hideIneligibility: { type: Boolean, attribute: 'hide-ineligibility' },
    tagBalance: { type: Boolean },
  };

  static styles = [style];

  render() {
    this.debug.log('rendered');
    const cardDetails = html` <div class="flex" data-e2e-ref="loan-card">
        <fs-icon
          .disabled="${!this.hideIneligibility && this.isLoanIneligible}"
          icon="fs-24-application"
        ></fs-icon>
        <div class="loan-info">
          ${this.showPlaceholder
            ? this.placeholder
            : html`
                <fs-heading
                  variant="${!this.hideIneligibility && this.isLoanIneligible
                    ? 'secondary'
                    : 'primary'}"
                  size="medium"
                  >${this.loan.lenderName}</fs-heading
                >
                <fs-body-text
                  variant="${!this.hideIneligibility && this.isLoanIneligible
                    ? 'disabled'
                    : 'secondary'}"
                >
                  ${currencyFormatter(this.loan.monthlyPayment)}/mo
                </fs-body-text>
                <fs-body-text
                  variant="${!this.hideIneligibility && this.isLoanIneligible
                    ? 'disabled'
                    : 'secondary'}"
                >
                  ${this.loan.remainingTerm} months
                </fs-body-text>
                ${this.loan.apr && this.loan.apr > 0
                  ? html`<fs-body-text
                      variant="${!this.hideIneligibility &&
                      this.isLoanIneligible
                        ? 'disabled'
                        : 'secondary'}"
                      >${this.loan.apr}% APR</fs-body-text
                    >`
                  : ''}
                <fs-body-text
                  variant="${!this.hideIneligibility && this.isLoanIneligible
                    ? 'disabled'
                    : 'secondary'}"
                >
                  ${currencyFormatter(this.loan.currentBalance)} payoff amount
                </fs-body-text>
              `}
        </div>
      </div>
      ${!this.hideIneligibility && !this.isEligible
        ? html`
            <fs-helper-text type="error" class="helper-text"
              >${this.ineligibleLoanMessage}</fs-helper-text
            >
          `
        : ''}
      ${this.showButton && (this.hideIneligibility || !this.isLoanIneligible)
        ? html`
            <fs-button
              class="card-button"
              variant="primary"
              .id="${this.loanId}"
              .name="${this.loanId}"
              @click="${this.selectThisLoan}"
              delegated-aria-label="Add this loan from ${this.loan.lenderName}"
              >${LoanService.getSelectLoanButtonCTA()}
            </fs-button>
          `
        : ''}`;
    return html`
      ${this.inline
        ? cardDetails
        : html`<fs-container
            class="container card ${classMap({ selected: this.selected })}"
          >
            ${cardDetails}
          </fs-container>`}
    `;
  }

  get placeholder() {
    if (this.hideIneligibility) {
      return html`<div class="placeholder delay">
        <fs-body-text size="tiny">
          Confirm the loan information below that is connected to this car.
        </fs-body-text>
      </div> `;
    }
    return html`<div class="placeholder">
      <fs-heading size="small">Add loan</fs-heading>
      <fs-body-text size="tiny">
        Add your current loan that is connected to this car.
      </fs-body-text>
    </div>`;
  }

  selectThisLoan() {
    this.dispatchEvent(
      new CustomEvent('selectedLoan', {
        detail: {
          loan: this.loan,
        },
      })
    );

    this.dispatchEvent(new CustomEvent('goToReviewStep'));
  }

  isHighLtv(currentBalance, wholeSaleValue) {
    const maxLtv = POLICY_LIMITS_CONST.MAX_LTV();
    return Number(currentBalance) / Number(wholeSaleValue) > maxLtv;
  }

  sendLoanAnalytics() {
    if (ApplicationService.isInAppDeclineDelayOn) {
      if (!this.showButton) {
        stratum.publishTag(TagKey.ICONIC_VEHICLE_AND_LOAN_LOAN_SHOWN, {
          replacements: {
            value: this.loan.lenderName,
          },
        });
      } else {
        stratum.publishTag(
          this.loan.isEligible
            ? TagKey.LOAN_LOOKUP_LOAN_FOUND_ELIGIBLE
            : TagKey.LOAN_LOOKUP_LOAN_FOUND_INELIGIBLE,
          {
            replacements: {
              value: `${this.loan.lenderName}`,
            },
          }
        );
      }
      if (this.tagBalance) {
        stratum.publishTag(
          this.loan.bureauUsed
            ? TagKey.LOAN_LOOKUP_CALCULATED_BALANCE_2
            : TagKey.LOAN_LOOKUP_TU_BALANCE_2
        );
      }
    } else {
      if (!this.isEligible) {
        REFI_VEHICLE_LOAN_CONST.TRACK_EVENT(
          tags.shared.loanCard.ineligible(
            LoanService.getIneligibleTag(this.loan)
          )
        );
        stratum.publishTag(TagKey.LOAN_LOOKUP_INELIGIBLE_LOAN, {
          replacements: {
            value: tags.shared.loanCard.ineligible(
              LoanService.getIneligibleTag(this.loan)
            ),
          },
        });
        stratum.publishTag(TagKey.LOAN_LOOKUP_REASON_FOR_TU_INELIGIBLE_LOAN, {
          replacements: {
            value: tags.shared.loanCard.ineligible(
              LoanService.getIneligibleTag(this.loan)
            ),
          },
        });
      }
      if (!LoanService.isManualLoan) {
        if (!this.loan.apr) {
          REFI_VEHICLE_LOAN_CONST.TRACK_EVENT(tags.shared.loanCard.noApr);
          stratum.publishTag(TagKey.LOAN_LOOKUP_REASON_FOR_TU_INELIGIBLE_LOAN, {
            replacements: { value: tags.shared.loanCard.noApr },
          });
        }
        if (!this.loan.openedDate) {
          REFI_VEHICLE_LOAN_CONST.TRACK_EVENT(tags.shared.loanCard.noOpenDate);
          stratum.publishTag(TagKey.LOAN_LOOKUP_REASON_FOR_TU_INELIGIBLE_LOAN, {
            replacements: { value: tags.shared.loanCard.noOpenDate },
          });
        }
        if (!this.loan.lenderName) {
          REFI_VEHICLE_LOAN_CONST.TRACK_EVENT(tags.shared.loanCard.noLender);
          stratum.publishTag(TagKey.LOAN_LOOKUP_REASON_FOR_TU_INELIGIBLE_LOAN, {
            replacements: { value: tags.shared.loanCard.noLender },
          });
        }
        if (!this.loan.lastPaymentDate) {
          REFI_VEHICLE_LOAN_CONST.TRACK_EVENT(
            tags.shared.loanCard.noLastPaymentDate
          );
          stratum.publishTag(TagKey.LOAN_LOOKUP_REASON_FOR_TU_INELIGIBLE_LOAN, {
            replacements: { value: tags.shared.loanCard.noLastPaymentDate },
          });
        }
        REFI_VEHICLE_LOAN_CONST.TRACK_EVENT(
          this.loan.bureauUsed
            ? tags.shared.loanCard.useCalculatedBalance
            : tags.shared.loanCard.useTuBalance
        );
        stratum.publishTag(
          this.loan.bureauUsed
            ? TagKey.LOAN_LOOKUP_CALCULATED_BALANCE
            : TagKey.LOAN_LOOKUP_TU_BALANCE
        );
      }
    }
  }

  get isLoanIneligible() {
    if (this.showPlaceholder) {
      return false;
    }
    let isIneligible = false;
    if (this.loan.ineligibleReasons == null) {
      isIneligible = false;
    } else {
      isIneligible = this.loan.ineligibleReasons.length > 0;
    }
    return isIneligible || this.inlineIneligible;
  }
}

customElements.define('refi-iconic-loan-card', RefiIconicLoanCard);
